import { computed, ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { usePolicy } from 'dashboard/composables/usePolicy';
import {
  MENTION_CATEGORIES,
  loadRecentMentionCategory,
  mentionItemKey,
  searchAllMentionCategories,
  searchMentionCategory,
} from 'shared/helpers/copilotMentionSearch';

export { MENTION_CATEGORIES } from 'shared/helpers/copilotMentionSearch';

export function parseMentionAtCursor(text, cursorPos) {
  const before = text.slice(0, cursorPos);
  const match = before.match(/(?:^|[\s\n])@([^\n@]*)$/);
  if (!match) return null;

  const atIndex = before.lastIndexOf('@');
  return {
    query: match[1],
    start: atIndex,
    end: cursorPos,
  };
}

export function useCopilotMentions() {
  const { shouldShow, isFeatureFlagEnabled } = usePolicy();

  const mentionedItems = ref([]);
  const menuOpen = ref(false);
  const mentionQuery = ref('');
  const mentionRange = ref(null);
  const searchResults = ref([]);
  const isSearching = ref(false);
  const selectedIndex = ref(0);
  const activeCategory = ref(null);

  const availableCategories = computed(() =>
    MENTION_CATEGORIES.filter(category => {
      if (category.featureFlag && !isFeatureFlagEnabled(category.featureFlag)) {
        return false;
      }
      return shouldShow(category.featureFlag, category.permissions, null);
    })
  );

  const showCategories = computed(
    () => menuOpen.value && !mentionQuery.value.trim() && !activeCategory.value
  );

  const showResults = computed(
    () =>
      menuOpen.value && (activeCategory.value || !!mentionQuery.value.trim())
  );

  const showTypeBadge = computed(
    () =>
      showResults.value && !activeCategory.value && !!mentionQuery.value.trim()
  );

  const menuItems = computed(() => {
    if (showCategories.value) {
      return availableCategories.value.map(category => ({
        type: 'category',
        id: category.id,
        category,
      }));
    }
    if (showResults.value) {
      return searchResults.value.map(item => ({
        type: 'result',
        id: mentionItemKey(item),
        item,
      }));
    }
    return [];
  });

  const runSearch = async (query, categoryId = null) => {
    isSearching.value = true;
    try {
      if (categoryId) {
        if (query.trim()) {
          searchResults.value = await searchMentionCategory(categoryId, query);
        } else {
          searchResults.value = await loadRecentMentionCategory(categoryId);
        }
      } else if (query.trim()) {
        const categoryIds = availableCategories.value.map(c => c.id);
        searchResults.value = await searchAllMentionCategories(
          categoryIds,
          query
        );
      } else {
        searchResults.value = [];
      }
    } catch {
      searchResults.value = [];
    } finally {
      isSearching.value = false;
      selectedIndex.value = 0;
    }
  };

  const debouncedSearch = useDebounceFn(runSearch, 200);

  function closeMenu() {
    menuOpen.value = false;
    mentionQuery.value = '';
    mentionRange.value = null;
    searchResults.value = [];
    selectedIndex.value = 0;
    activeCategory.value = null;
  }

  function openMentionTrigger({ query, start, end }) {
    menuOpen.value = true;
    mentionQuery.value = query;
    mentionRange.value = { start, end };

    if (query.trim()) {
      debouncedSearch(query, activeCategory.value);
      return;
    }

    if (activeCategory.value) {
      debouncedSearch('', activeCategory.value);
    }
  }

  function syncMentionFromInput(text, cursorPos) {
    const trigger = parseMentionAtCursor(text, cursorPos);
    if (!trigger) {
      closeMenu();
      return;
    }

    openMentionTrigger({
      query: trigger.query,
      start: trigger.start,
      end: trigger.end,
    });
  }

  /** TipTap: sync from ProseMirror positions { query, from, to }. */
  function syncMentionFromEditor(trigger) {
    if (!trigger) {
      closeMenu();
      return;
    }

    openMentionTrigger({
      query: trigger.query,
      start: trigger.from,
      end: trigger.to,
    });
  }

  function selectCategory(categoryId) {
    activeCategory.value = categoryId;
    selectedIndex.value = 0;
    debouncedSearch(mentionQuery.value, categoryId);
  }

  function addMentionItem(item) {
    const key = mentionItemKey(item);
    if (!mentionedItems.value.some(m => mentionItemKey(m) === key)) {
      mentionedItems.value = [...mentionedItems.value, item];
    }
  }

  function removeMention(itemKey) {
    mentionedItems.value = mentionedItems.value.filter(
      item => mentionItemKey(item) !== itemKey
    );
  }

  function pruneMentionsToKeys(keys) {
    const keySet = keys instanceof Set ? keys : new Set(keys);
    mentionedItems.value = mentionedItems.value.filter(item =>
      keySet.has(mentionItemKey(item))
    );
  }

  function clearMentions() {
    mentionedItems.value = [];
    closeMenu();
  }

  function applyMentionSelection(item, text) {
    if (!mentionRange.value) return text;

    const { start, end } = mentionRange.value;
    const before = text.slice(0, start);
    const after = text.slice(end);
    const nextText = `${before}${after}`.replace(/\s{2,}/g, ' ').trimStart();

    addMentionItem(item);
    closeMenu();
    return nextText;
  }

  function moveSelection(direction) {
    if (!menuItems.value.length) return;

    const lastIndex = menuItems.value.length - 1;
    if (direction === 'up') {
      selectedIndex.value =
        selectedIndex.value === 0 ? lastIndex : selectedIndex.value - 1;
    } else {
      selectedIndex.value =
        selectedIndex.value === lastIndex ? 0 : selectedIndex.value + 1;
    }
  }

  function getSelectedItem() {
    return menuItems.value[selectedIndex.value] || null;
  }

  return {
    mentionedItems,
    menuOpen,
    mentionQuery,
    searchResults,
    isSearching,
    selectedIndex,
    showCategories,
    showResults,
    showTypeBadge,
    availableCategories,
    menuItems,
    syncMentionFromInput,
    syncMentionFromEditor,
    mentionRange,
    applyMentionSelection,
    addMentionItem,
    removeMention,
    pruneMentionsToKeys,
    clearMentions,
    closeMenu,
    moveSelection,
    getSelectedItem,
    selectCategory,
    mentionItemKey,
  };
}
